const nativeAppStorageKey = 'cinema:native-app';
const nativeDeviceStorageKey = 'cinema:native-device-id';

declare global {
  interface Window {
    __AI_CINEMA_NATIVE_APP__?: boolean;
    NativeFileDownload?: {
      postMessage: (message: string) => void;
    };
    NativeSessionStorage?: {
      postMessage: (message: string) => void;
    };
  }
}

export function isNativeAppRuntime(): boolean {
  if (typeof window === 'undefined') return false;

  const source = new URLSearchParams(window.location.search).get('mmp_source');
  if (source === 'app' || window.__AI_CINEMA_NATIVE_APP__ === true) {
    try {
      window.sessionStorage.setItem(nativeAppStorageKey, '1');
    } catch {
      // The URL marker still identifies the native runtime when storage is blocked.
    }
    return true;
  }

  try {
    return window.sessionStorage.getItem(nativeAppStorageKey) === '1';
  } catch {
    return false;
  }
}

export function isAndroidNativeAppRuntime(): boolean {
  if (typeof navigator === 'undefined') return false;
  return isNativeAppRuntime() && /Android/i.test(navigator.userAgent);
}

export function nativeAppRequestHeaders(): Record<string, string> {
  if (!isNativeAppRuntime()) return {};
  let deviceID = '';
  try {
    deviceID = window.localStorage.getItem(nativeDeviceStorageKey) || '';
  } catch {
    deviceID = '';
  }
  return {
    'X-Client-Platform': 'app',
    ...(deviceID ? { 'X-Client-Device-ID': deviceID } : {}),
  };
}

export function persistNativeMemberSession(value: string | null): void {
  if (!isNativeAppRuntime() || !window.NativeSessionStorage?.postMessage) return;
  window.NativeSessionStorage.postMessage(value ?? '__CLEAR__');
}

export function downloadTextFile(filename: string, content: string): boolean {
  if (isNativeAppRuntime() && window.NativeFileDownload?.postMessage) {
    const bytes = new TextEncoder().encode(content);
    let binary = '';
    for (const byte of bytes) binary += String.fromCharCode(byte);
    const dataUrl = `data:text/plain;charset=utf-8;base64,${window.btoa(binary)}`;
    window.NativeFileDownload.postMessage(JSON.stringify({ dataUrl, filename }));
    return true;
  }

  const url = URL.createObjectURL(new Blob([content], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}

export async function copyTextToClipboard(content: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(content);
    return true;
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = content;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    textarea.remove();
    return copied;
  }
}
